import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import InboxIcon from '@mui/icons-material/Inbox';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import WidgetsIcon from '@mui/icons-material/Widgets';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { canViewPlanning, UserRole } from '@pe/shared';
import { useState } from 'react';
import { Link as RouterLink, NavLink, Outlet, useNavigate } from 'react-router-dom';

import { NotificationBell } from '../../features/notifications/NotificationBell';
import { useLogout } from '../../hooks/useAuth';
import { env } from '../../lib/env';
import { useAuthStore } from '../../stores/auth-store';
import { BrandMark, brandTextGradient } from '../BrandLogo';

const DRAWER_WIDTH = 256;

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactElement;
  adminOnly?: boolean;
  planningOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/app', label: 'Tableau de bord', icon: <DashboardIcon /> },
  { to: '/app/tickets', label: 'Tickets', icon: <InboxIcon /> },
  { to: '/app/tickets/new', label: 'Nouveau ticket', icon: <AddCircleOutlineIcon /> },
  { to: '/app/assistant', label: 'Assistant IT', icon: <SmartToyOutlinedIcon /> },
  { to: '/app/modules', label: 'Modules', icon: <WidgetsIcon />, planningOnly: true },
  { to: '/app/planning', label: 'Planning', icon: <CalendarMonthIcon />, planningOnly: true },
  { to: '/app/pilotage', label: 'Pilotage IT', icon: <FlagOutlinedIcon />, planningOnly: true },
  { to: '/app/kb', label: 'Base de connaissances', icon: <MenuBookIcon />, planningOnly: true },
  { to: '/app/reports', label: 'Rapports', icon: <AssessmentIcon />, planningOnly: true },
  { to: '/app/admin', label: 'Administration', icon: <AdminPanelSettingsIcon />, adminOnly: true },
];

const ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Administrateur',
  IT: 'Équipe IT',
  DIRECTION: 'Direction',
  EMPLOYEE: 'Collaborateur',
};

export function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials =
    [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('') ||
    (user?.email[0]?.toUpperCase() ?? '?');

  function handleLogout() {
    setAnchor(null);
    logout.mutate(undefined, {
      onSettled: () => navigate('/login', { replace: true }),
    });
  }

  const items = NAV_ITEMS.filter((item) => {
    if (item.adminOnly) return user?.role === UserRole.ADMIN;
    if (item.planningOnly) return user && canViewPlanning(user.role);
    return true;
  });

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <BrandMark size={38} />
          <Box sx={{ lineHeight: 1.1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, ...brandTextGradient }}>
              Premium Energy
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Suivi IT interne
            </Typography>
          </Box>
        </Stack>
      </Toolbar>
      <Divider />
      <Box
        component="nav"
        aria-label="Navigation principale"
        sx={{ overflow: 'auto', flexGrow: 1, py: 1.5 }}
      >
        <List disablePadding>
          {items.map((item) => (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              end={item.to === '/app'}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: 14, fontWeight: 'inherit' }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            p: 1.25,
            borderRadius: 2,
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: 14,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {user?.firstName || user?.lastName
                ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
                : user?.email}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {user ? (ROLE_LABEL[user.role] ?? user.role) : ''}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        color="inherit"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'rgba(255, 255, 255, 0.85)',
          boxShadow: '0 2px 10px rgba(47, 43, 61, 0.06)',
        }}
      >
        <Toolbar sx={{ gap: 1, px: { xs: 2, md: 3 } }}>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            aria-label="Ouvrir le menu"
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, flexGrow: 1 }}
          >
            <BrandMark size={28} />
            <Typography
              component={RouterLink}
              to="/app"
              variant="subtitle1"
              sx={{ fontWeight: 700, color: 'text.primary', textDecoration: 'none' }}
            >
              {env.appName}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
          <NotificationBell />
          <Tooltip title="Mon compte">
            <IconButton
              onClick={(e) => setAnchor(e.currentTarget)}
              aria-label="Menu utilisateur"
              aria-haspopup="menu"
              aria-expanded={Boolean(anchor)}
              size="small"
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            slotProps={{ list: { 'aria-label': 'Menu utilisateur', dense: true } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { mt: 1, minWidth: 240 } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem component={RouterLink} to="/app/settings" onClick={() => setAnchor(null)}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Mon compte
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Se déconnecter
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="aside"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="Navigation latérale"
      >
        {/* Drawer mobile (temporary) */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Drawer desktop (permanent) */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Toolbar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2.5, md: 3.5 },
            maxWidth: 1440,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Outlet />
        </Box>
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: 12,
            borderTop: '1px solid',
            borderColor: 'divider',
            mt: 'auto',
          }}
        >
          © {new Date().getFullYear()} Premium Energy — Outil interne
        </Box>
      </Box>
    </Box>
  );
}
